/**
 * Trilho de camadas do celular, na borda esquerda do mapa (08/10/2026): um
 * botão de ícone por camada, que liga e desliga com um toque, como o trilho do
 * desktop (ui/Rail) — antes, toda troca de camada no celular passava pela
 * folha inferior, que cobre metade do mapa. No topo, "⚙ Camadas: ajustes"
 * abre a folha de sempre (ui/MobileSheet): a grade continua sendo a porta dos
 * ajustes de cada camada, do resumo do que está ligado, do "NOVO" e do aviso.
 *
 * - Em pé: uma coluna de 44 px a partir de y 64 (abaixo do chip do topo), que
 *   termina 8 px acima da assinatura do pyForTraCC e do crédito da foto de fundo; o
 *   que não cabe rola por dentro, com a borda esmaecida do lado escondido.
 * - Deitado: duas colunas, a altura toda (o chip e a assinatura passam para a
 *   direita do trilho, `--trilho-fim`).
 *
 * O botão é só ícone: depois de cada toque, um balão ao lado diz o nome e o
 * estado ("Nível · ligada") por 1,5 s; o toque longo mostra só o balão, sem
 * alternar — para quem quer saber o que o ícone é antes de ligar. O estado
 * não depende só da cor: ligada tem o ✓ da grade.
 *
 * Com a folha de camadas aberta o trilho some (como o play e a legenda); ao
 * fechar, o foco volta ao "⚙".
 *
 * Recolher (09/10/2026): o "⌃" no pé do trilho o recolhe a um botão só, com o
 * número de camadas ligadas, e o mapa fica livre na borda; tocar nele expande
 * de novo. Lembrado no navegador (ui/trilhoRecolhido).
 *
 * No "Próximos dias" (18/10/2026), como no desktop: os botões das camadas da
 * previsão no lugar dos das observadas, que ficam encolhidas em "Observado"
 * logo abaixo (um toque estende); o "⚙" abre os ajustes da previsão. O trilho
 * não recolhe no modo (a preferência de fora do modo fica como estava).
 */
import clsx from 'clsx';
import { Check, ChevronDown, ChevronUp, Layers, SlidersHorizontal } from 'lucide-react';
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';
import { BotaoDaPrevisaoNoTrilho, ObservadoNoTrilho, useVariaveisNoTrilho } from '../previsao/BotoesDaPrevisao';
import { usePrevisaoStore } from '../previsao/store';
import { useUiStore } from '../state/ui';
import { BORDA_PX } from './layoutCelular';
import { CHIP } from './MapActions';
import { marcarVisto } from './novidade';
import { getPanels, type PanelDef } from './panels';
import { DEFAULT_TONE, TONE_TRILHO } from './tone';
import { useTrilhoStore } from './trilhoRecolhido';
import { useDeitado } from './useMediaQuery';

/** Quanto tempo (ms) o balão fica ao lado do botão depois do toque. */
export const DURACAO_DO_BALAO_MS = 1500;
/** Segurar o dedo por isto (ms) mostra o balão sem alternar a camada. */
export const TOQUE_LONGO_MS = 450;

/** Topo do trilho (px abaixo da área segura): abaixo da marca do topo (12 + 44 + 8) em pé; deitado, na linha do topo (12). */
const TOPO_EM_PE = 64;
const TOPO_DEITADO = 12;

/** O balão aberto: o texto e a altura (px, no <main>) do meio do botão. */
interface Balao {
  texto: string;
  top: number;
  left: number;
}

type AoTocar = (def: PanelDef, botao: HTMLElement, ligada: boolean, alternou: boolean) => void;

/** "ligada · GeoColor + 1", "desligada", e o aviso, para o leitor de tela (`aria-describedby`). */
export function descricaoDoBotao(ligada: boolean, resumo: string | null, aviso: boolean): string {
  const estado = ligada ? `ligada${resumo ? ` · ${resumo}` : ''}` : 'desligada';
  return aviso ? `${estado} · dados indisponíveis` : estado;
}

/**
 * Até onde o trilho pode descer: px a partir do pé do <main>. O que fica no pé
 * EMBAIXO dele (em pé: o relógio do pé e o crédito da foto de fundo, quando a foto
 * está na tela) empurra o fim para 8 px acima do topo; deitado nada
 * fica embaixo dele, e o fim é a borda.
 */
function useFimDoTrilho(trilhoRef: RefObject<HTMLElement | null>, ativo: boolean): number {
  const [fim, setFim] = useState(BORDA_PX);
  useLayoutEffect(() => {
    const trilho = trilhoRef.current;
    const host = trilho?.parentElement;
    if (!ativo || !trilho || !host) return;
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => medir());
    const observados = new Set<Element>();
    // Procurados a cada medida: o pé do mapa é montado de novo ao voltar do "Próximos dias" (e troca
    // pelo da previsão), e um elemento guardado do primeiro render já não estaria na tela — o trilho
    // descia até o fundo, por trás do relógio, e o "Recolher" ficava escondido (relato do usuário, 29/10/2026).
    const embaixo = () => [
      host.querySelector<HTMLElement>('[data-relogio-do-pe]'),
      host.querySelector<HTMLElement>('[data-pyfortracc-mark]'),
      host.querySelector<HTMLElement>('.amaview-attribution'),
      // "Próximos dias": a escala de cores fica acima do pé, de borda a borda.
      host.querySelector<HTMLElement>('[data-legenda-previsao]'),
    ];
    const medir = () => {
      const h = host.getBoundingClientRect();
      const t = trilho.getBoundingClientRect();
      let proximo = BORDA_PX;
      for (const el of embaixo()) {
        if (el && !observados.has(el)) {
          observados.add(el);
          ro?.observe(el);
        }
        const r = el?.getBoundingClientRect();
        if (!r || r.width <= 0 || r.height <= 0) continue;
        if (r.left < t.right && r.right > t.left) proximo = Math.max(proximo, Math.ceil(h.bottom - r.top) + BORDA_PX);
      }
      setFim((f) => (f === proximo ? f : proximo));
    };
    medir();
    window.addEventListener('resize', medir);
    ro?.observe(host);
    // O que entra e sai do mapa (o pé da observação e o da previsão) mede de novo.
    const mo = typeof MutationObserver === 'undefined' ? null : new MutationObserver(medir);
    mo?.observe(host, { childList: true });
    return () => {
      window.removeEventListener('resize', medir);
      ro?.disconnect();
      mo?.disconnect();
    };
  }, [trilhoRef, ativo]);
  return fim;
}

/**
 * A setinha que avisa que há camadas escondidas acima ou abaixo (sugestão do usuário, 29/10/2026):
 * o esmaecer da borda sozinho passava despercebido. Só indica — o toque passa para o botão embaixo.
 */
function SetaDeRolagem(props: { lado: 'acima' | 'abaixo' }) {
  const Icone = props.lado === 'acima' ? ChevronUp : ChevronDown;
  return (
    <span
      aria-hidden
      data-seta-rolagem={props.lado}
      className={clsx(
        'pointer-events-none absolute left-1/2 z-10 inline-flex -translate-x-1/2 items-center justify-center rounded-full border border-ink-600 bg-ink-900/95 px-1.5 text-ink-200 shadow',
        props.lado === 'acima' ? 'top-0.5' : 'bottom-0.5',
      )}
    >
      <Icone className="size-3.5" />
    </span>
  );
}

/** Há botões escondidos acima / abaixo da parte visível da rolagem? (para esmaecer só essa borda) */
function useBordasEscondidas(rolagemRef: RefObject<HTMLElement | null>, ativo: boolean, chave?: unknown): { acima: boolean; abaixo: boolean } {
  const [bordas, setBordas] = useState({ acima: false, abaixo: false });
  useLayoutEffect(() => {
    const el = rolagemRef.current;
    if (!ativo || !el) return;
    const medir = () => {
      const acima = el.scrollTop > 1;
      const abaixo = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
      setBordas((b) => (b.acima === acima && b.abaixo === abaixo ? b : { acima, abaixo }));
    };
    medir();
    el.addEventListener('scroll', medir, { passive: true });
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(medir);
    ro?.observe(el);
    return () => {
      el.removeEventListener('scroll', medir);
      ro?.disconnect();
    };
    // `chave`: a lista que rola trocou de elemento (entrar e sair do "Próximos dias").
  }, [rolagemRef, ativo, chave]);
  return bordas;
}

const FADE = '14px';
function mascara(b: { acima: boolean; abaixo: boolean }): string | undefined {
  if (b.acima && b.abaixo) return `linear-gradient(to bottom, transparent, black ${FADE}, black calc(100% - ${FADE}), transparent)`;
  if (b.acima) return `linear-gradient(to bottom, transparent, black ${FADE})`;
  if (b.abaixo) return `linear-gradient(to bottom, black calc(100% - ${FADE}), transparent)`;
  return undefined;
}

/**
 * "⚙ Camadas: ajustes": abre a folha de camadas (a grade, com a engrenagem de cada uma).
 * No "Próximos dias", os ajustes da previsão (a folha dela).
 */
export function BotaoAjustesDasCamadas(props: { ref?: RefObject<HTMLButtonElement | null>; previsao?: boolean }) {
  const rotulo = props.previsao ? 'Ajustes da previsão' : 'Camadas: ajustes';
  return (
    <button
      ref={props.ref}
      type="button"
      onClick={() => {
        if (!props.previsao) return useUiStore.getState().set({ sheetOpen: true, sheetStage: 'camadas' });
        const s = usePrevisaoStore.getState();
        if (s.painel === 'ajustes') s.fecharPainel();
        else s.abrirAjustes();
      }}
      // O nome começa por "Camadas" (como o antigo botão da folha); deitado, "Ajustes" aparece escrito.
      aria-label={rotulo}
      title={rotulo}
      data-trilho-ajustes=""
      className="flex h-10 w-10 shrink-0 items-center justify-center gap-1.5 rounded-[10px] text-ink-100 transition-colors hover:bg-ink-700/70 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-mata-400 deitado:w-full"
    >
      <SlidersHorizontal className={clsx('size-5 shrink-0', props.previsao ? 'text-violet-300' : 'text-mata-400')} aria-hidden />
      <span className="hidden text-[13px] font-semibold deitado:inline" aria-hidden>
        Ajustes
      </span>
    </button>
  );
}

/** Um botão do trilho: o interruptor da camada (`def.layer`). */
export function BotaoDoTrilho(props: { def: PanelDef; aoTocar: AoTocar }) {
  const { def, aoTocar } = props;
  // `def` é fixo por instância (chave = id) e sempre tem `layer` (o trilho filtra): a ordem dos hooks não muda.
  const ligada = def.layer!.useOn();
  const resumo = def.useSummary?.() ?? null;
  const aviso = def.useWarning?.() ?? false;
  const descId = useId();
  const ref = useRef<HTMLButtonElement>(null);
  const relogio = useRef<number | undefined>(undefined);
  const longo = useRef(false);
  const tone = def.tone ?? DEFAULT_TONE;
  const Icon = def.icon;

  const cancelar = () => {
    window.clearTimeout(relogio.current);
    relogio.current = undefined;
  };
  useEffect(() => cancelar, []);

  const apertar = (e: ReactPointerEvent) => {
    longo.current = false;
    cancelar();
    if (e.button !== 0) return;
    relogio.current = window.setTimeout(() => {
      // Toque longo: só o balão; o clique que vem ao soltar não alterna.
      longo.current = true;
      if (ref.current) aoTocar(def, ref.current, ligada, false);
    }, TOQUE_LONGO_MS);
  };

  const tocar = () => {
    cancelar();
    if (longo.current) {
      longo.current = false;
      return;
    }
    // Como o trilho do desktop: liga/desliga a camada inteira. Não abre nem troca o painel (não há painel aberto).
    marcarVisto(def.id);
    def.layer!.setOn(!ligada);
    if (ref.current) aoTocar(def, ref.current, !ligada, true);
  };

  return (
    <button
      ref={ref}
      type="button"
      onPointerDown={apertar}
      onPointerUp={cancelar}
      onPointerCancel={cancelar}
      onPointerLeave={cancelar}
      onClick={tocar}
      // O toque longo é do balão, não do menu do navegador.
      onContextMenu={(e) => e.preventDefault()}
      // Nome estável ("Nível"); o estado vem do aria-pressed, e o resumo e o aviso, da descrição.
      aria-label={def.label}
      aria-pressed={ligada}
      aria-describedby={descId}
      data-trilho-camada={def.id}
      className={clsx(
        'relative flex size-10 shrink-0 touch-pan-y items-center justify-center rounded-[10px] transition-colors select-none [-webkit-touch-callout:none]',
        'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-mata-400',
        ligada ? TONE_TRILHO[tone] : 'text-ink-300 hover:bg-ink-700/60',
      )}
    >
      <Icon className="size-5" aria-hidden />
      {/* Marca de "ligada" além da cor (legível para quem não distingue as cores), a mesma da grade. */}
      {ligada && (
        <span className="absolute right-0.5 bottom-0.5 grid size-3 place-items-center rounded-full bg-white ring-2 ring-ink-900" aria-hidden>
          <Check className="size-2 text-ink-950" strokeWidth={4} />
        </span>
      )}
      {aviso && <span className="absolute top-1 left-1 size-1.5 rounded-full bg-yellow-300 ring-1 ring-ink-900" aria-hidden />}
      <span id={descId} className="sr-only">
        {descricaoDoBotao(ligada, resumo, aviso)}
      </span>
    </button>
  );
}

/** "⌃ Recolher", no pé do trilho. */
function BotaoRecolher(props: { ref?: RefObject<HTMLButtonElement | null>; controla: string; aoTocar: () => void }) {
  return (
    <button
      ref={props.ref}
      type="button"
      onClick={props.aoTocar}
      aria-label="Recolher as camadas"
      aria-expanded="true"
      aria-controls={props.controla}
      title="Recolher as camadas"
      data-trilho-recolher=""
      className="flex h-10 w-10 shrink-0 items-center justify-center gap-1.5 rounded-[10px] text-ink-300 transition-colors hover:bg-ink-700/70 hover:text-white focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-mata-400 deitado:w-full"
    >
      <ChevronUp className="size-5 shrink-0" aria-hidden />
      <span className="hidden text-[13px] font-semibold deitado:inline" aria-hidden>
        Recolher
      </span>
    </button>
  );
}

/** Conta uma camada ligada para o botão do trilho recolhido (cada camada tem a sua loja). */
function Ligada(props: { def: PanelDef; aoMudar: (id: string, ligada: boolean) => void }) {
  const ligada = props.def.layer!.useOn();
  const { def, aoMudar } = props;
  useEffect(() => aoMudar(def.id, ligada), [def.id, ligada, aoMudar]);
  return null;
}

/** O trilho recolhido: um botão com o ícone das camadas e quantas estão ligadas; tocar expande. */
function TrilhoRecolhido(props: { ref?: RefObject<HTMLButtonElement | null>; camadas: PanelDef[]; aoTocar: () => void }) {
  const [ligadas, setLigadas] = useState<Record<string, boolean>>({});
  const aoMudar = useCallback((id: string, ligada: boolean) => setLigadas((l) => (l[id] === ligada ? l : { ...l, [id]: ligada })), []);
  const n = Object.values(ligadas).filter(Boolean).length;
  const texto = n === 1 ? '1 camada ligada' : `${n} camadas ligadas`;
  return (
    <>
      {props.camadas.map((def) => (
        <Ligada key={def.id} def={def} aoMudar={aoMudar} />
      ))}
      <button
        ref={props.ref}
        type="button"
        onClick={props.aoTocar}
        aria-label={`Mostrar as camadas (${texto})`}
        aria-expanded="false"
        title="Mostrar as camadas"
        data-trilho-expandir=""
        className="relative flex size-10 shrink-0 items-center justify-center rounded-[10px] text-mata-400 transition-colors hover:bg-ink-700/70 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-mata-400"
      >
        <Layers className="size-5" aria-hidden />
        {n > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-white px-1 text-[10px] leading-none font-semibold text-ink-950 tabular-nums ring-2 ring-ink-900"
            aria-hidden
          >
            {n}
          </span>
        )}
      </button>
    </>
  );
}

/** O balão ao lado do botão tocado: só visual (o leitor de tela ouve o `aria-live` do trilho). */
export function RotuloDoToque(props: { balao: Balao | null }) {
  if (!props.balao) return null;
  return (
    <div
      aria-hidden
      data-balao-do-trilho=""
      className="pointer-events-none absolute z-30 -translate-y-1/2 rounded-lg border border-ink-600/90 bg-ink-900/95 px-2.5 py-1.5 text-xs font-semibold whitespace-nowrap text-white shadow-lg"
      style={{ top: props.balao.top, left: props.balao.left }}
    >
      {props.balao.texto}
    </div>
  );
}

export function TrilhoDeCamadas() {
  const folhaAberta = useUiStore((s) => s.sheetOpen);
  const modo = usePrevisaoStore((s) => s.modo);
  const observadoAberto = usePrevisaoStore((s) => s.observadoAberto);
  const variaveisDaPrevisao = useVariaveisNoTrilho();
  const deitado = useDeitado();
  const trilhoRef = useRef<HTMLElement>(null);
  const rolagemRef = useRef<HTMLDivElement>(null);
  const ajustesRef = useRef<HTMLButtonElement>(null);
  const recolherRef = useRef<HTMLButtonElement>(null);
  const expandirRef = useRef<HTMLButtonElement>(null);
  // No modo o trilho não recolhe: os botões da previsão ficam à vista.
  const recolhido = useTrilhoStore((s) => s.recolhido) && !modo;
  const listaId = useId();
  const [balao, setBalao] = useState<Balao | null>(null);
  const [anuncio, setAnuncio] = useState('');
  const fim = useFimDoTrilho(trilhoRef, !folhaAberta);
  const bordas = useBordasEscondidas(rolagemRef, !folhaAberta && !recolhido, modo);

  // Recolher ou expandir pelo botão: o foco passa ao botão que o substitui.
  const alternou = useRef(false);
  useEffect(() => {
    if (!alternou.current) return;
    alternou.current = false;
    (recolhido ? expandirRef : recolherRef).current?.focus({ preventScroll: true });
  }, [recolhido]);
  const alternar = (recolher: boolean) => {
    alternou.current = true;
    setBalao(null);
    useTrilhoStore.getState().setRecolhido(recolher);
    setAnuncio(recolher ? 'Camadas recolhidas' : 'Camadas à mostra');
  };

  // Ao fechar a folha (✕, Escape ou voltar do celular), o foco volta ao "⚙" que a abriu.
  const estavaAberta = useRef(folhaAberta);
  useEffect(() => {
    if (estavaAberta.current && !folhaAberta) ajustesRef.current?.focus({ preventScroll: true });
    estavaAberta.current = folhaAberta;
  }, [folhaAberta]);

  // O balão some sozinho; um toque novo recomeça a contagem.
  useEffect(() => {
    if (!balao) return;
    const t = window.setTimeout(() => setBalao(null), DURACAO_DO_BALAO_MS);
    return () => window.clearTimeout(t);
  }, [balao]);

  if (folhaAberta) return null;

  const mostrarBalao = (texto: string, botao: HTMLElement) => {
    const host = trilhoRef.current?.parentElement?.getBoundingClientRect();
    const trilho = trilhoRef.current?.getBoundingClientRect();
    const b = botao.getBoundingClientRect();
    if (host && trilho) setBalao({ texto, top: b.top + b.height / 2 - host.top, left: trilho.right - host.left + BORDA_PX });
  };
  const aoTocar: AoTocar = (def, botao, ligada, alternou) => {
    mostrarBalao(`${def.label} · ${ligada ? 'ligada' : 'desligada'}`, botao);
    if (alternou) setAnuncio(`${def.label} ${ligada ? 'ligada' : 'desligada'}`);
  };
  const aoTocarNaPrevisao = (texto: string, botao: HTMLElement) => {
    mostrarBalao(texto, botao);
    setAnuncio(`${texto} no mapa`);
  };

  const topo = deitado ? TOPO_DEITADO : TOPO_EM_PE;
  const camadas = getPanels().filter((p) => p.layer);
  return (
    <>
      <nav
        ref={trilhoRef}
        aria-label="Camadas"
        // A ficha do mapa conta o trilho como ocupado; deitado, como área reservada (map/popupArea `medirTrilho`).
        data-trilho-do-mapa=""
        className={clsx(CHIP, 'absolute z-20 flex w-[var(--trilho-largura)] flex-col rounded-xl p-px')}
        data-recolhido={recolhido ? '' : undefined}
        style={{
          left: `calc(${BORDA_PX}px + env(safe-area-inset-left))`,
          top: `calc(env(safe-area-inset-top) + ${topo}px)`,
          maxHeight: `calc(100% - env(safe-area-inset-top) - ${topo + fim}px)`,
        }}
      >
        {recolhido ? (
          <TrilhoRecolhido ref={expandirRef} camadas={camadas} aoTocar={() => alternar(false)} />
        ) : modo ? (
          <>
            <BotaoAjustesDasCamadas ref={ajustesRef} previsao />
            <span className="mx-1.5 my-1 h-px shrink-0 bg-ink-600" aria-hidden />
            <div className="relative flex min-h-0 flex-col">
            <div
              ref={rolagemRef}
              onScroll={() => setBalao(null)}
              className="flex min-h-0 flex-col gap-0.5 overflow-y-auto overscroll-contain [scrollbar-width:none] deitado:grid deitado:grid-cols-2 [&::-webkit-scrollbar]:hidden"
              style={{ touchAction: 'pan-y', maskImage: mascara(bordas), WebkitMaskImage: mascara(bordas) }}
            >
              <div className="contents" role="group" aria-label="Camadas da previsão">
                {variaveisDaPrevisao.map((v) => (
                  <BotaoDaPrevisaoNoTrilho key={v} v={v} aoTocar={aoTocarNaPrevisao} />
                ))}
              </div>
              <span className="mx-1.5 my-1 h-px shrink-0 bg-ink-600 deitado:col-span-2" aria-hidden />
              <ObservadoNoTrilho controla={listaId} />
              {observadoAberto && (
                <div id={listaId} className="contents" role="group" aria-label="Camadas observadas">
                  {camadas.map((def) => (
                    <BotaoDoTrilho key={def.id} def={def} aoTocar={aoTocar} />
                  ))}
                </div>
              )}
            </div>
            {bordas.acima && <SetaDeRolagem lado="acima" />}
            {bordas.abaixo && <SetaDeRolagem lado="abaixo" />}
            </div>
          </>
        ) : (
          <>
            <BotaoAjustesDasCamadas ref={ajustesRef} />
            <span className="mx-1.5 my-1 h-px shrink-0 bg-ink-600" aria-hidden />
            <div className="relative flex min-h-0 flex-col">
              <div
                id={listaId}
                ref={rolagemRef}
                onScroll={() => setBalao(null)}
                className="flex min-h-0 flex-col gap-0.5 overflow-y-auto overscroll-contain [scrollbar-width:none] deitado:grid deitado:grid-cols-2 [&::-webkit-scrollbar]:hidden"
                style={{ touchAction: 'pan-y', maskImage: mascara(bordas), WebkitMaskImage: mascara(bordas) }}
              >
                {camadas.map((def) => (
                  <BotaoDoTrilho key={def.id} def={def} aoTocar={aoTocar} />
                ))}
              </div>
              {bordas.acima && <SetaDeRolagem lado="acima" />}
              {bordas.abaixo && <SetaDeRolagem lado="abaixo" />}
            </div>
            <span className="mx-1.5 my-1 h-px shrink-0 bg-ink-600" aria-hidden />
            <BotaoRecolher ref={recolherRef} controla={listaId} aoTocar={() => alternar(true)} />
          </>
        )}
      </nav>
      <RotuloDoToque balao={balao} />
      <p className="sr-only" aria-live="polite">
        {anuncio}
      </p>
    </>
  );
}
